import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { AuthProvider } from './lib/auth';
import './styles/chats.css';
import { isAndroid, isIOS } from './lib/device';
import { applyStoredDisplay } from './lib/display';

// Theme and list density before the first paint (Gary, 18 Oct).
applyStoredDisplay();

// iPhones and iPads: text follows the phone's Larger Text setting (chats.css, 16 Oct).
if (isIOS) document.documentElement.classList.add('chats-ios');

// The app sits exactly over the part of the screen the keyboard leaves visible, as a native
// app does (Gary, 16 Oct). On iPhone the keyboard does not shrink the page: it slides the
// visible area down the page, which put the message box at the top of the screen with white
// below it, and after closing left the app short with a white strip under it.
// While the keyboard is up: the app is pinned to the visible area (--app-top, --app-h) and
// sheets and toasts sit above the keyboard (--kb). Once it is down: the variables go, and
// the app fills the screen by CSS alone, so nothing can be left stale.
// These are the one inline values: they change as the keyboard moves.
const KEYBOARD_MIN = 80;
// Android shrinks the whole page for the keyboard instead (interactive-widget in index.html),
// so there it shows as the page being shorter than its full height while typing.
let tallest = window.innerHeight;
function typingInField(): boolean {
  const el = document.activeElement;
  return !!el && (el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && !['checkbox', 'radio', 'button', 'submit', 'file', 'range'].includes((el as HTMLInputElement).type)) || (el as HTMLElement).isContentEditable);
}
function fitToScreen() {
  const vv = window.visualViewport;
  const root = document.documentElement;
  const full = window.innerHeight;
  if (!typingInField()) tallest = Math.max(tallest, full);
  const height = vv ? vv.height : full;
  const top = vv ? vv.offsetTop : 0;
  const keyboard = full - height > KEYBOARD_MIN || (isAndroid && typingInField() && tallest - full > KEYBOARD_MIN * 2);
  root.classList.toggle('chats-kb-open', keyboard);
  if (!keyboard) markShortWindow(full);
  if (keyboard) {
    root.style.setProperty('--app-top', `${Math.round(top)}px`);
    root.style.setProperty('--app-h', `${Math.round(height)}px`);
    root.style.setProperty('--kb', `${Math.max(0, Math.round(full - height - top))}px`);
  } else {
    root.style.removeProperty('--app-top');
    root.style.removeProperty('--app-h');
    root.style.removeProperty('--kb');
    if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
  }
}
/**
 * An iPhone home-screen app installed before 18 Oct 16:25 SAST keeps the see-through status bar it was
 * added with (WebKit bug 260508), and iOS gives it a window short by the status bar's height, ending above
 * the home bar (Settings, Screen check: page 793 on an 852 screen, notch top 59). The phone draws that strip
 * itself, so the tab bar and message box need no home-bar space of their own there (chats.css,
 * html.chats-short-window). Checked only while the keyboard is down.
 */
let safeProbe: HTMLDivElement | null = null;
function markShortWindow(full: number) {
  if (!document.body) return;
  if (!safeProbe) {
    safeProbe = document.createElement('div');
    safeProbe.className = 'chats-safe-probe';
    document.body.appendChild(safeProbe);
  }
  const safeTop = parseFloat(getComputedStyle(safeProbe).paddingTop) || 0;
  const portrait = window.screen.height > window.screen.width;
  const short = window.matchMedia('(display-mode: standalone)').matches && safeTop > 0 && portrait
    && Math.abs(window.screen.height - full - safeTop) <= 2;
  document.documentElement.classList.toggle('chats-short-window', short);
}

// iPhone animates the keyboard, and the visible area settles a moment after focus changes:
// measure again as it settles.
function fitSoon() {
  [0, 120, 350, 700].forEach((ms) => window.setTimeout(fitToScreen, ms));
}
fitToScreen();
// No pinch zoom (mobile review, 18 Oct): once zoomed, every drag slid the app sideways. Larger Text covers reading.
document.addEventListener('gesturestart', (e) => e.preventDefault());
document.addEventListener('gesturechange', (e) => e.preventDefault());
window.visualViewport?.addEventListener('resize', fitToScreen);
window.visualViewport?.addEventListener('scroll', fitToScreen);
window.addEventListener('resize', fitToScreen);
window.addEventListener('orientationchange', () => { tallest = 0; fitSoon(); });
document.addEventListener('focusin', fitSoon);
document.addEventListener('focusout', fitSoon);
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') fitSoon(); });

// A new version reaches everyone (Gary, 15 Oct: an open app kept running yesterday's screens).
// Every few minutes, and whenever the app comes back to the front, compare this page's script
// with the live one; when it changed, reload the next time the app is opened or brought back,
// so nobody loses a half-typed message.
const loadedScript = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')?.src ?? '';
let updateWaiting = false;
async function checkForUpdate() {
  if (!loadedScript || updateWaiting) return;
  try {
    const html = await (await fetch(import.meta.env.BASE_URL, { cache: 'no-store' })).text();
    const live = /<script[^>]+type="module"[^>]+src="([^"]+)"/.exec(html)?.[1];
    if (live && !loadedScript.endsWith(live)) updateWaiting = true;
  } catch { /* offline: try again later */ }
}
window.setInterval(() => void checkForUpdate(), 5 * 60_000);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') { void checkForUpdate(); return; }
  if (updateWaiting) window.location.reload();
  else void checkForUpdate().then(() => { if (updateWaiting && !document.activeElement?.matches('textarea, input')) window.location.reload(); });
});

/** The copy shown inside Sentinel (built with CHATS_BASE=/chats/). */
const EMBEDDED = import.meta.env.BASE_URL !== '/';
if (EMBEDDED) document.documentElement.classList.add('chats-in-sentinel');

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <BrowserRouter basename={EMBEDDED ? import.meta.env.BASE_URL.replace(/\/$/, '') : undefined}>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>,
);

// The service worker serves the app offline and shows phone notifications.
// Not inside Sentinel: Sentinel's own service worker looks after its address.
if ('serviceWorker' in navigator && import.meta.env.PROD && !EMBEDDED) {
  navigator.serviceWorker.register('/sw.js').catch(() => undefined);
}
